import { Component, Host, h, Prop, State, Watch, Listen, Event, EventEmitter } from '@stencil/core';
import { getCurrentMediaQuery } from '../../../utils/getCurrentMediaQuery';
import { LabelParser } from '../sew-ws-header/helpers/labels.helpers';
import { Tracking } from '../sew-ws-header/tracking/tracking';
import { Labels } from '../sew-ws-header/types/labels.types';
import { NavItem } from '../sew-ws-header/types/navigation.types';
import { Permission } from '../sew-ws-header/types/permission.types';
import { SewUser } from '@sew/auth-service';
import { User } from '../sew-ws-header/types/user.types';

@Component({
    tag: 'sew-ws-navigation',
    styleUrl: 'sew-ws-navigation.scss',
    shadow: true,
})
export class SewWsNavigation {
    @Event() navigateLogin: EventEmitter<string>;
    @Event() notPermittedItemClicked: EventEmitter<string>;

    @Prop({ attribute: 'navHomeItem', reflect: true }) navHomeItem: NavItem;
    @Prop() user: SewUser | User | null = null;
    @Prop() permissions: Permission[] | null = null;
    @Prop() labels: Labels;
    @Prop({ reflect: true }) hasMainNavigation: boolean;
    @Prop() language = '';
    @Prop() tracking?: Tracking;

    @State() navColumns: NavItem[] = [];
    @State() navColumnsLength = 4;
    @State() scrollContainers: HTMLElement[] = [];
    @State() private labelParser: LabelParser;

    /**
     * Workaround to rerender the DOM. Set to empty object ({}) each time the DOM should be rerendered
     */
    @State() forceRerender = {};

    @Listen('resize', { target: 'window' })
    onResize(): void {
        this.checkMaxNavColumnsLength();
        this.checkNavColumns();
    }

    /**
     * Tracks if any Element outside of the Navigation Flyout was clicked.
     * If this is the case, close the Navigation Flyout closes.
     * @param eventWindowClicked Event emitted on click on window
     */
    @Listen('click', { target: 'window' })
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    checkForClickOutside(eventWindowClicked): void {
        const path =
            eventWindowClicked.path || (eventWindowClicked.composedPath && eventWindowClicked.composedPath());

        if (path.every(el => this.navColumnsElements.every(navEl => navEl !== el))) {
            this.closeNavigation.emit({ close: true, path });
        }
    }

    @Event({ composed: true, bubbles: true, cancelable: false }) closeNavigation: EventEmitter;

    @Watch('navHomeItem')
    watchNavHomeItem(): void {
        // rerender navColumns when navHomeItem changes
        this.navColumns = [this.navHomeItem, ...this.navColumns.slice(1)];
    }

    @Watch('user')
    watchUser(): void {
        this.forceRerender = {};
    }

    @Watch('labels')
    public watchLabels(): void {
        this.labelParser = new LabelParser(this.labels);
    }

    private navColumnsRef: HTMLDivElement;
    private navColumnsElements = [];

    componentWillLoad(): void {
        this.parseLanguage(this.language);
        this.checkMaxNavColumnsLength();
        this.navColumns = [this.navHomeItem];
        this.labelParser = new LabelParser(this.labels);
        // same as comment in childClicked function
        this.rerenderDom();
    }

    componentDidLoad(): void {
        this.navColumnsRef.style.setProperty(
            '--nav-column-width',
            String(this.navColumnsRef.querySelector('.nav-column').clientWidth) + 'px'
        );
    }

    private parseLanguage(locale: string) {
        if (locale.length === 5) {
            let language = locale.slice(0, 2);
            let country = locale.slice(3, 5);
            language = language.toLowerCase();
            country = country.toUpperCase();
            this.language = `${language}_${country}`;
        }
    }

    private childClicked(event: MouseEvent, item: NavItem): void {
        if (item.children.length) {
            // prevent navigation for items with children - open submenu instead
            event.preventDefault();
            event.stopPropagation();

            const itemLevel = this.hasMainNavigation ? item.level - 1 : item.level;

            this.navColumns =
                itemLevel < this.navColumns.length
                    ? [...this.navColumns.slice(0, itemLevel), item]
                    : [...this.navColumns, item];
            /* Workaround to render the scroll arrows correctly.
            When a new column is rendered, because it was added to navColumns, scrollContainer Element
            of current index is undefined while rendering DOM. The arrrows wont show, so a rerender
            has to be forced, when the scrollContainer Element is set*/
            this.rerenderDom();
        } else {
            // For leaf items (no children), let the browser handle the link naturally
            // This automatically supports middle click, ctrl+click, target="_blank", etc.
            if (this.tracking) {
                this.tracking.trackETrackerEvent(event.target as Element);
            }
            // Don't prevent default - let browser handle the link navigation
        }
        this.checkNavColumns();
    }

    private backClicked(event: MouseEvent): void {
        // prevent navigation
        event.preventDefault();
        // prevent function from beeing called twice
        event.stopPropagation();
        this.navColumns = this.navColumns.slice(0, this.navColumns.length - 1);
        this.checkNavColumns();
    }

    private scroll = (index: number, direction?: number): void => {
        const element = this.scrollContainers[index];
        direction && element.scrollBy(0, 50 * direction);
        this.forceRerender = {};
    };

    private checkNavColumns = () => {
        if (this.navColumns.length > this.navColumnsLength) {
            const navColumnsOverflow =
                this.navColumnsRef.querySelector('.nav-column').clientWidth *
                (this.navColumns.length - this.navColumnsLength);

            this.navColumnsRef.style.setProperty(
                'transform',
                `translate3d(-${String(navColumnsOverflow)}px, 0, 0`
            );
        } else {
            this.navColumnsRef.style.setProperty('transform', 'translate3d(0, 0, 0)');
        }
    };

    private checkMaxNavColumnsLength = () => {
        if (getCurrentMediaQuery() === 1) {
            this.navColumnsLength = 1;
        } else if (getCurrentMediaQuery() === 2) {
            this.navColumnsLength = 2;
        } else if (getCurrentMediaQuery() < 3) {
            this.navColumnsLength = 3;
        } else {
            this.navColumnsLength = 4;
        }
    };

    private rerenderDom() {
        setTimeout(() => {
            this.forceRerender = {};
        }, 100);
    }

    private handleLockItemClicked(item: NavItem) {
        const loginRequired = item.isLocked;
        if (loginRequired) {
            this.navigateLogin.emit(item.link);
        } else {
            this.notPermittedItemClicked.emit(item.role_required);
        }
    }

    public render() {
        this.navColumnsElements = [];

        return (
            <Host>
                <div class="nav-grid">
                    <div class="nav-container">
                        {this.navColumns.length > this.navColumnsLength && (
                            <sew-ws-menu-link type="return">
                                <button onClick={e => this.backClicked(e)}>
                                    {this.labelParser.getLabel('CMS_HEADER_BACK')}
                                </button>
                            </sew-ws-menu-link>
                        )}

                        <div class="nav-columns" ref={ref => (this.navColumnsRef = ref)}>
                            {this.navColumns.map((navItem, index) => (
                                <div
                                    class="nav-column"
                                    ref={el => this.navColumnsElements.push(el)}
                                    style={{ 'z-index': String(100 - index) }}
                                >
                                    {this.hasMainNavigation && this.navColumns.length - 1 === index && (
                                        <button
                                            class="close-btn"
                                            onClick={() => this.closeNavigation.emit({ close: true })}
                                        >
                                            <sew-icon icon="close" />
                                        </button>
                                    )}
                                    {this.scrollContainers[index] &&
                                        this.scrollContainers[index].scrollTop !== 0 && (
                                            <button
                                                class={{
                                                    'scroll-btn': true,
                                                    'scroll-btn--up': true,
                                                }}
                                                onClick={() => this.scroll(index, -1)}
                                            >
                                                <sew-icon
                                                    class="svg"
                                                    icon="arrow-chevron-up"
                                                    size="xl"
                                                ></sew-icon>
                                            </button>
                                        )}
                                    <div class="panel-container">
                                        <sew-ws-menu-link type="parent">
                                            <a
                                                href={navItem.link}
                                                target={navItem.target}
                                                innerHTML={navItem.name}
                                                data-sew-trackme-event-category={`Navigation[${this.language}]`}
                                                data-sew-trackme-event-object={navItem.eTrackerObject}
                                                data-sew-trackme-event-action="Click"
                                            />
                                        </sew-ws-menu-link>
                                        <div
                                            class="panel-list"
                                            ref={el => (this.scrollContainers[index] = el as HTMLElement)}
                                            onScroll={() => this.scroll(index)}
                                        >
                                            {navItem.children
                                                .filter(
                                                    item => !item.authorizationRequired || item.hasPermission
                                                )
                                                .map(childItem => {
                                                    if (childItem.isLocked || !childItem.hasPermission) {
                                                        return (
                                                            <sew-ws-menu-link
                                                                type="locked"
                                                                info={
                                                                    childItem.isLocked
                                                                        ? this.labelParser.getLabel(
                                                                              'CMS_HEADER_NAV_LOGIN_REQUIRED'
                                                                          )
                                                                        : this.labelParser.getLabel(
                                                                              'CMS_HEADER_NAV_NO_PERMISSION'
                                                                          )
                                                                }
                                                            >
                                                                <a
                                                                    href={childItem.link}
                                                                    target={childItem.target}
                                                                    onClick={e => (
                                                                        e.preventDefault(),
                                                                        this.handleLockItemClicked(childItem)
                                                                    )}
                                                                    innerHTML={childItem.name}
                                                                />
                                                            </sew-ws-menu-link>
                                                        );
                                                    } else {
                                                        return (
                                                            <sew-ws-menu-link
                                                                current={
                                                                    this.navColumns.slice(
                                                                        -this.navColumnsLength
                                                                    )[index + 1] &&
                                                                    this.navColumns.slice(
                                                                        -this.navColumnsLength
                                                                    )[index + 1] === childItem
                                                                }
                                                                type={
                                                                    childItem.children.length
                                                                        ? 'default'
                                                                        : 'childest'
                                                                }
                                                            >
                                                                <a
                                                                    data-sew-trackme-event-category={`Navigation[${this.language}]`}
                                                                    data-sew-trackme-event-object={
                                                                        childItem.eTrackerObject
                                                                    }
                                                                    data-sew-trackme-event-action="Click"
                                                                    href={childItem.link}
                                                                    target={childItem.target}
                                                                    onClick={e =>
                                                                        this.childClicked(e, childItem)
                                                                    }
                                                                    innerHTML={childItem.name}
                                                                ></a>
                                                            </sew-ws-menu-link>
                                                        );
                                                    }
                                                })}
                                        </div>
                                    </div>
                                    {this.scrollContainers[index] &&
                                        this.scrollContainers[index].scrollHeight !==
                                            this.scrollContainers[index].scrollTop +
                                                this.scrollContainers[index].clientHeight && (
                                            <button
                                                class={{
                                                    'scroll-btn': true,
                                                    'scroll-btn--down': true,
                                                }}
                                                onClick={() => this.scroll(index, 1)}
                                            >
                                                <sew-icon
                                                    class="svg"
                                                    icon="arrow-chevron-down"
                                                    size="xl"
                                                ></sew-icon>
                                            </button>
                                        )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
